import React, { useState, useRef, useCallback } from "react"
import { Button, Card, Space, Badge, Divider, Switch, ConfigProvider, theme } from "antd"
import {
    CloseOutlined,
    DragOutlined,
    WalletOutlined,
    EnvironmentOutlined,
    DeleteOutlined,
} from "@ant-design/icons"
import settingsStore from "../../stores/settingsStore"

export default function FloatingVariableList({ variables, setSelectedText, deleteText }) {
    const [visible, setVisible] = useState(false)
    const settings = settingsStore((state) => state.settings)
    const [position, setPosition] = useState({ x: window.innerWidth - 400, y: 100 })
    const [isDragging, setIsDragging] = useState(false)
    const [totalPrice, setTotalPrice] = useState(0)
    const dragRef = useRef({ startX: 0, startY: 0, offsetX: 0, offsetY: 0 })

    const handleMouseDown = (e) => {
        setIsDragging(true)
        dragRef.current = {
            startX: e.clientX,
            startY: e.clientY,
            offsetX: position.x,
            offsetY: position.y,
        }
    }

    const handleMouseMove = (e) => {
        if (!isDragging) return

        const deltaX = e.clientX - dragRef.current.startX
        const deltaY = e.clientY - dragRef.current.startY

        setPosition({
            x: dragRef.current.offsetX + deltaX,
            y: dragRef.current.offsetY + deltaY,
        })
    }

    const handleMouseUp = () => {
        setIsDragging(false)
    }

    React.useEffect(() => {
        if (isDragging) {
            window.addEventListener("mousemove", handleMouseMove)
            window.addEventListener("mouseup", handleMouseUp)
            return () => {
                window.removeEventListener("mousemove", handleMouseMove)
                window.removeEventListener("mouseup", handleMouseUp)
            }
        }
    }, [isDragging])

    React.useEffect(() => {
        let price = settings.find(({ name }) => name == "createBanner")?.price || 0

        variables.forEach((v) => {
            price += settings.find(({ name }) => name == v.variable.key)?.price || 0
        })

        setTotalPrice(price)
    }, [variables.length])

    return (
        <div className="scrollbar">
            {/* Floating Action Button */}
            <Button
                type="primary"
                shape="circle"
                size="large"
                icon={<WalletOutlined />}
                onClick={() => setVisible(!visible)}
                style={{
                    position: "fixed",
                    bottom: 30,
                    right: 30,
                    width: 60,
                    height: 60,
                    fontSize: 24,
                    boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
                    zIndex: 999,
                }}
            />

            {/* Draggable Floating Popup */}
            {visible && (
                <div
                    style={{
                        direction: "rtl",
                        position: "fixed",
                        left: position.x,
                        top: position.y,
                        zIndex: 1000,
                        userSelect: isDragging ? "none" : "auto",
                    }}
                >
                    {" "}
                    <ConfigProvider theme={{ algorithm: theme.darkAlgorithm }}>
                        <Card
                            style={{
                                width: 360,
                                boxShadow: "0 8px 24px rgba(0,0,0,0.12)",
                                borderRadius: 8,
                            }}
                            bodyStyle={{ padding: 0 }}
                        >
                            {/* Draggable Header */}
                            <div
                                onMouseDown={handleMouseDown}
                                style={{
                                    padding: "16px 20px",
                                    background: "#1e1e1e",
                                    color: "#e0e0e0",
                                    cursor: isDragging ? "grabbing" : "grab",
                                    display: "flex",
                                    justifyContent: "space-between",
                                    alignItems: "center",
                                    borderTopLeftRadius: 8,
                                    borderTopRightRadius: 8,
                                }}
                            >
                                <Space>
                                    <DragOutlined />
                                    <strong>متغیر ها</strong>
                                    <Badge
                                        count={variables.length}
                                        style={{ backgroundColor: "#52c41a" }}
                                    />
                                </Space>
                                <CloseOutlined
                                    onClick={() => setVisible(false)}
                                    style={{ cursor: "pointer", fontSize: 16 }}
                                />
                            </div>

                            {/* Content */}
                            <div style={{ maxHeight: 400, overflowY: "auto" }}>
                                <Space direction="vertical" style={{ width: "100%" }}>
                                    {/* Add ranksystem price if used any */}
                                    {variables.some((v) => v.variable.key.startsWith("rs-")) && (
                                        <div className="hover:bg-gray-900">
                                            <div
                                                className="cursor-pointer flex justify-between"
                                                style={{
                                                    padding: 12,
                                                    alignItems: "start",
                                                }}
                                            >
                                                <div style={{ display: "flex", gap: 12 }}>
                                                    <EnvironmentOutlined
                                                        style={{
                                                            fontSize: 20,
                                                            color: "#1890ff",
                                                            marginTop: 2,
                                                        }}
                                                    />
                                                    <div style={{ flex: 1 }}>
                                                        <strong>رنک سیستم</strong>
                                                        <p
                                                            style={{
                                                                margin: "4px 0 0 0",
                                                                color: "#666",
                                                            }}
                                                        >
                                                            استفاده از متغیر رنک سیستم
                                                        </p>
                                                        <small style={{ color: "#999" }}>
                                                            {settings
                                                                ?.find(
                                                                    ({ name }) =>
                                                                        name == "useRanksystem"
                                                                )
                                                                ?.price.toLocaleString("en-US") ||
                                                                0}{" "}
                                                            تومان
                                                        </small>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    )}
                                    {variables.map((v, index) => (
                                        <div className="hover:bg-gray-900" key={index}>
                                            <div
                                                onClick={() => setSelectedText(v.id)}
                                                className="cursor-pointer flex justify-between"
                                                style={{
                                                    padding: 12,
                                                    alignItems: "start",
                                                }}
                                            >
                                                <div style={{ display: "flex", gap: 12 }}>
                                                    <EnvironmentOutlined
                                                        style={{
                                                            fontSize: 20,
                                                            color: "#1890ff",
                                                            marginTop: 2,
                                                        }}
                                                    />
                                                    <div style={{ flex: 1 }}>
                                                        <strong>{v.variable.key}</strong>
                                                        <p
                                                            style={{
                                                                margin: "4px 0 0 0",
                                                                color: "#666",
                                                            }}
                                                        >
                                                            {v.variable.description}
                                                        </p>
                                                        <small style={{ color: "#999" }}>
                                                            {settings
                                                                ?.find(
                                                                    ({ name }) =>
                                                                        name == v.variable.key
                                                                )
                                                                ?.price.toLocaleString("en-US") ||
                                                                0}{" "}
                                                            تومان
                                                        </small>
                                                    </div>
                                                </div>
                                                <div className="mt-2">
                                                    <button
                                                        onClick={() => deleteText(v.id)}
                                                        className="hover:text-white hover:bg-gray-950 rounded-sm px-2 py-1"
                                                    >
                                                        <DeleteOutlined
                                                            style={{
                                                                color: "#dc3545",
                                                                fontSize: 20,
                                                            }}
                                                        />
                                                    </button>
                                                </div>
                                            </div>
                                            {index < variables.length - 1 && (
                                                <Divider style={{ margin: 0 }} />
                                            )}
                                        </div>
                                    ))}
                                </Space>
                            </div>

                            {/* Footer */}
                            <div
                                style={{
                                    padding: "12px 16px",
                                    borderTop: "1px solid #1e1e1e",
                                    background: "#1e1e1e",
                                }}
                            >
                                <Space style={{ width: "100%", justifyContent: "space-between" }}>
                                    <Button type="link" size="small" style={{ padding: 0 }}>
                                        {totalPrice?.toLocaleString("en-US")} تومان
                                    </Button>
                                </Space>
                            </div>
                        </Card>
                    </ConfigProvider>
                </div>
            )}
        </div>
    )
}
